// File: app/src/utils/effortCue.js
//
// Fallback effort cues. The prompt requires an intensityTarget on every Work
// step and the schema now enforces it, but a model can still return an empty
// one — and a missing cue is how "stop 2-4 reps short of failure" silently
// never reaches the lifter. This fills only what is missing, from the
// exercise's movement pattern and the session's goals (docs/training_science.md
// §2, effort by goal).

import { exerciseOf } from "../config/exercises";

const RESISTANCE = new Set([
  "squat", "hinge", "lunge", "push_horizontal", "push_vertical",
  "pull_horizontal", "pull_vertical", "isolation", "carry", "core", "rotation",
]);

/** A sensible effort cue for one Work step, given the session context. */
export const defaultEffortCue = (node, { styles = [], beginner = false } = {}) => {
  const exercise = exerciseOf(node);
  const pattern = exercise?.pattern;
  const list = styles.map((s) => String(s).toLowerCase());
  const reps = node?.unit === "reps" ? Number(node.value) : null;

  if (pattern === "mobility" || pattern === "breathing") return "Slow and controlled";
  if (pattern === "jump" || pattern === "throw" || (list.includes("power") && pattern === "olympic")) {
    return "Explosive - stop the set if speed drops";
  }
  if (pattern === "locomotion" || node?.unit === "cal" || (node?.unit === "s" && !RESISTANCE.has(pattern))) {
    if (list.includes("cardio") && !list.some((s) => ["hiit", "tabata"].includes(s))) {
      return "Zone 2 - conversational pace";
    }
    if (list.some((s) => ["hiit", "tabata"].includes(s))) return "All-out";
    return "Hard but repeatable";
  }
  if (beginner) return "Leave 3+ reps in reserve";
  if (node?.unit === "s") return "Hold perfect form";
  if (reps != null && reps <= 6) return "RIR 2-3";
  if (list.includes("hypertrophy") || (reps != null && reps >= 8)) return "RIR 1-2";
  return "RPE 7-8";
};

/**
 * Fill empty intensityTarget fields on Work steps, in place. Warm-up and
 * cool-down steps get a gentle cue rather than a training one.
 */
export const fillMissingEffortCues = (plan, profile = {}, onRepair) => {
  const styles = Array.isArray(profile.workoutStyles)
    ? profile.workoutStyles
    : [profile.workoutStyle].filter(Boolean);
  const beginner =
    String(profile.fitnessLevel || "").toLowerCase() === "beginner" ||
    ["beginner", "novice"].includes(String(profile.trainingExperience || "").toLowerCase());
  const visit = (node, inPhase) => {
    if (!node || typeof node !== "object") return;
    const phase = inPhase || /warm[\s-]?up|cool[\s-]?down/i.test(node.label || "") ||
      node.type === "Warmup" || node.type === "Cooldown";
    if (node.type === "Work" && !String(node.intensityTarget || "").trim()) {
      node.intensityTarget = phase ? "Easy - preparation" : defaultEffortCue(node, { styles, beginner });
      if (onRepair) onRepair("repair.effortCue");
    }
    (node.children || []).forEach((c) => visit(c, phase));
  };
  visit(plan, false);
  return plan;
};
